import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { Flame, ListChecks, RefreshCw, Snowflake, Sparkles, Sun, Sunrise, TreeDeciduous } from 'lucide-react-native';
import { ComponentType } from 'react';
import { Image, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { Fonts, Palette } from '@/constants/theme';
import { currentSeasonLabel } from '@/lib/tasks';

import { ProfileRing } from './ProfileRing';
import { SeasonRing } from './SeasonRing';

const SEASON_ICON: Record<
  string,
  ComponentType<{ size?: number; color?: string; strokeWidth?: number }>
> = {
  Spring: Sunrise,
  Summer: Sun,
  Fall: TreeDeciduous,
  Winter: Snowflake,
};

export function Greeting({
  doneThisWeek,
  ringPct,
  backgroundUri,
  homeName,
  ownerFirstName,
  greetingSource,
  seasonPct,
  seasonName,
  streak = 0,
  systemsToTune = 0,
  attributesBlank = 0,
  firstBlankLabel = null,
  firstBlankField = null,
  homeCount = 1,
  suggestReplan = false,
  topRightActions,
}: {
  doneThisWeek: number;
  ringPct: number;
  backgroundUri?: string | null;
  homeName?: string | null;
  ownerFirstName?: string | null;
  greetingSource?: 'first_name' | 'home_name';
  seasonPct: number;
  seasonName: 'Spring' | 'Summer' | 'Fall' | 'Winter';
  streak?: number;
  /** Count of systems that have a subtype-eligible kind but no
   *  subtype picked yet. When > 0, render a "Tune my plan" chip
   *  next to the season/streak chips that links to /profile-setup. */
  systemsToTune?: number;
  /** Count of profile attribute fields still blank (zip, hvac, water
   *  source, etc.). When > 0, render a "Finish my profile" chip that
   *  routes to /property/edit so the user can see WHICH field. */
  attributesBlank?: number;
  /** Label of the first blank field, if any — used in the chip body
   *  so the user sees "Add water source" instead of a generic
   *  "Finish my profile". */
  firstBlankLabel?: string | null;
  /** Field key of the first blank attribute (e.g. 'heating_fuel') —
   *  used to deep-link the "Add X" chip into the matching Tune card. */
  firstBlankField?: string | null;
  /** Total homes the user has. When 2+, an "at [Home Name]" tagline
   *  renders below the date so the user always knows which home Home
   *  is showing data for. Single-home users see nothing extra. */
  homeCount?: number;
  /** True when the active property's plan is more than ~11 months old
   *  (see shouldSuggestReplan). Renders a honey-tinted chip suggesting
   *  the user re-tune their year so seasonal context, lifestyle flags,
   *  and rule additions all flow through. */
  suggestReplan?: boolean;
  /** Render slot for icon actions in the upper-right corner of the
   *  greeting card, ABOVE the percentages. Matches the My Home /
   *  Schedule submenu pattern so the user finds the same affordances
   *  in the same spot across the three primary surfaces. */
  topRightActions?: React.ReactNode;
}) {
  const router = useRouter();
  const now = new Date();
  const greeting = greetingFor(now);
  const dateLabel = now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const season = currentSeasonLabel(now);
  const SeasonIcon = SEASON_ICON[season.name];

  // Honor the user's greeting-source preference (first_name | home_name).
  // Fall back to the other field if the chosen one is empty, then to 'Home'.
  const first = ownerFirstName?.trim();
  const home = homeName?.trim();
  const display =
    greetingSource === 'home_name'
      ? home || first || 'Home'
      : first || home || 'Home';

  return (
    <View>
      {/* Always show a hero image — user's photo if set, default art
          otherwise. The default-profile asset gives every fresh account
          a warm look right out of onboarding instead of a blank cream
          band above the greeting. */}
      <View style={styles.heroWrap}>
        <Image
          source={
            backgroundUri
              ? { uri: backgroundUri }
              : require('@/assets/images/default-profile.png')
          }
          style={styles.heroImage}
        />
        <LinearGradient
          colors={['rgba(241,232,216,0.0)', 'rgba(241,232,216,0.85)', Palette.sand]}
          locations={[0, 0.7, 1]}
          style={StyleSheet.absoluteFill}
        />
        {/* Submenu icons float in the upper-right corner of the hero
            image — higher than the percentages below — so the user
            sees them immediately on opening the Today tab. Matches
            the My Home / Schedule pattern of "actions live in the
            top-right of the page header." */}
        {topRightActions ? (
          <View style={styles.heroActions} pointerEvents="box-none">
            {topRightActions}
          </View>
        ) : null}
      </View>

      <View style={[styles.header, styles.headerOverlay]}>
        <View style={styles.row}>
          <View style={{ flex: 1 }}>
            <Text style={styles.greeting}>
              {greeting},{'\n'}
              <Text style={styles.greetingEm}>{display}</Text>
            </Text>
            <Text style={styles.sub}>{dateLabel}</Text>
            {homeCount > 1 && home ? (
              // Multi-home only — tells the user which home they're
              // looking at. Single tap opens the HomeSwitcher (same
              // affordance as the My Home tab title) so they can
              // jump to another home from Home tab in one move.
              <Pressable
                onPress={() => router.push('/(tabs)/my-home' as never)}
                accessibilityLabel={`Showing ${home}. Tap to switch home.`}
                style={({ pressed }) => [
                  styles.activeHomeRow,
                  pressed && { opacity: 0.7 },
                ]}>
                <Text style={styles.activeHomeText}>
                  at <Text style={styles.activeHomeName}>{home}</Text>
                </Text>
              </Pressable>
            ) : null}
          </View>
          <View style={styles.ringRow}>
            <SeasonRing pct={seasonPct} season={seasonName} />
            <Pressable
              onPress={() => router.push('/property/edit' as never)}
              hitSlop={6}
              accessibilityLabel="Edit my home — fill in details to raise this percentage"
              style={({ pressed }) => pressed && { opacity: 0.85 }}>
              <ProfileRing pct={ringPct} />
            </Pressable>
          </View>
        </View>

        {/*
          The chip row lives inside a header View that has 22px of side
          padding. If we leave the ScrollView inside that padded box, the
          visible scroll area is `screenWidth - 44`, the chips clip at an
          arbitrary inner edge, and the row feels boxed-in. Pulling the
          ScrollView OUT to the screen edge with negative horizontal
          margins lets chips genuinely flow off the page when scrolled,
          while the contentContainerStyle's paddingHorizontal: 22 keeps
          the first chip aligned to the header gutter. iOS bounce-scroll
          and Android over-scroll glow now hit the real screen edge,
          which reads as the intended "more here, swipe to see it" cue.
        */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.chipsScroll}
          contentContainerStyle={styles.chips}>
          {/* Tap the season chip to jump into the Schedule pre-filtered
              to the current season — gives the user a one-click path
              from "I'm in spring" to "show me what spring needs." */}
          <Pressable
            onPress={() => router.push({
              pathname: '/(tabs)/schedule',
              params: { season: season.name.toLowerCase() },
            } as never)}
            accessibilityLabel={`Open ${season.name} tasks`}
            style={({ pressed }) => [styles.chip, styles.chipSeason, pressed && { opacity: 0.85 }]}>
            <SeasonIcon size={12} color={Palette.forest} strokeWidth={1.6} />
            <Text style={[styles.chipText, { color: Palette.forest }]}>
              {seasonChipLabel(season.name)} · wk {season.week}
            </Text>
          </Pressable>
          {/* Streak chip removed — Achievements UI was already deleted in
              an earlier wave and the standalone streak signal felt out of
              place without the surrounding game-loop framing. The same
              completed-task history is one tap away via the bottom tab
              bar → Schedule → Completed. */}
          {/*
            "Tune my plan" chip — only shows when there are systems
            with subtype options that haven't been selected yet. Sits
            alongside the season/streak chips so it reads as a
            friendly nudge, not a sticky banner. Honey-deep tint
            mirrors the Sparkles icon vibe (warm + inviting).
          */}
          {systemsToTune > 0 ? (
            <Pressable
              onPress={() => router.push('/profile-setup' as never)}
              accessibilityLabel={`Tune ${systemsToTune} system${systemsToTune === 1 ? '' : 's'}`}
              style={({ pressed }) => [styles.chip, styles.chipTune, pressed && { opacity: 0.85 }]}>
              <Sparkles size={12} color={Palette.honeyDeep} strokeWidth={2} />
              <Text style={[styles.chipText, { color: Palette.honeyDeep }]}>
                Tune <Text style={styles.chipNum}>{systemsToTune}</Text> {systemsToTune === 1 ? 'system' : 'systems'}
              </Text>
            </Pressable>
          ) : null}
          {/*
            Annual replan nudge — only when last_replan_at is older
            than ~11 months. Routes to /(tabs)/my-home where the user
            can tap the Replan icon (or we could deep-link the dialog
            in a future pass). Honey-deep palette matches the Tune
            chip — both are "freshen up the plan" verbs.
          */}
          {suggestReplan ? (
            <Pressable
              onPress={() => router.push('/(tabs)/my-home' as never)}
              accessibilityLabel="Replan your year — it's been a while"
              style={({ pressed }) => [styles.chip, styles.chipTune, pressed && { opacity: 0.85 }]}>
              <RefreshCw size={12} color={Palette.honeyDeep} strokeWidth={2} />
              <Text style={[styles.chipText, { color: Palette.honeyDeep }]}>
                Time to replan
              </Text>
            </Pressable>
          ) : null}
          {/*
            "Finish my profile" chip — surfaces remaining blank home
            attributes (water source, hvac, etc.) right on Home so a
            stuck-at-96% user can see exactly what's left without
            hunting through the My Home tab. Tap routes to /profile-setup.
            When we know the specific field name we lead with it
            ("Add water source") so the action reads concrete.
          */}
          {attributesBlank > 0 ? (
            <Pressable
              onPress={() =>
                // Deep-link to the specific card for the first blank
                // field. "Add heating fuel" should land ON the heating
                // fuel card, not on whichever card the queue happens
                // to start with.
                router.push(
                  firstBlankField
                    ? `/profile-setup?field=${encodeURIComponent(firstBlankField)}` as never
                    : '/profile-setup' as never
                )
              }
              accessibilityLabel={
                firstBlankLabel
                  ? `Add ${firstBlankLabel.toLowerCase()} to finish your profile`
                  : `${attributesBlank} home detail${attributesBlank === 1 ? '' : 's'} still blank`
              }
              style={({ pressed }) => [styles.chip, styles.chipFinish, pressed && { opacity: 0.85 }]}>
              <ListChecks size={12} color={Palette.forest} strokeWidth={2} />
              <Text style={[styles.chipText, { color: Palette.forest }]}>
                {firstBlankLabel && attributesBlank === 1
                  ? `Add ${firstBlankLabel.toLowerCase()}`
                  : firstBlankLabel
                  ? `Add ${firstBlankLabel.toLowerCase()} + ${attributesBlank - 1} more`
                  : `Finish profile · ${attributesBlank} blank`}
              </Text>
            </Pressable>
          ) : null}
          {/* Tap to jump into the Past tasks log — quickest path from
              "I did stuff this week" to seeing the receipts. */}
          <Pressable
            onPress={() =>
              router.push({ pathname: '/(tabs)/schedule', params: { completed: '1' } } as never)
            }
            accessibilityLabel="Open past tasks"
            style={({ pressed }) => [styles.chip, styles.chipDone, pressed && { opacity: 0.85 }]}>
            <Text style={[styles.chipText, { color: Palette.honeyDeep }]}>
              <Text style={styles.chipNum}>{doneThisWeek}</Text> done this week
            </Text>
          </Pressable>
        </ScrollView>
      </View>
    </View>
  );
}

function greetingFor(d: Date): string {
  const h = d.getHours();
  if (h < 5) return 'Late night';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Good night';
}

function seasonChipLabel(name: string): string {
  if (name === 'Winter' || name === 'Fall') return 'Heating season';
  if (name === 'Summer') return 'Cooling season';
  return 'Spring is here';
}

const styles = StyleSheet.create({
  heroWrap: {
    width: '100%',
    height: 200,
    overflow: 'hidden',
  },
  heroImage: {
    width: '100%',
    height: '100%',
  },
  header: {
    paddingHorizontal: 22,
    paddingTop: 8,
  },
  headerOverlay: {
    marginTop: -100, // overlap onto the bottom of the hero image
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  // Submenu icons absolutely positioned in the upper-right of the
  // hero image — as high as we can get them without overlapping the
  // app header. Floats above the gradient + greeting text below.
  heroActions: {
    position: 'absolute',
    top: 10,
    right: 14,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    zIndex: 2,
  },
  ringRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'flex-start',
  },
  greeting: {
    fontFamily: Fonts.serif,
    fontSize: 27,
    lineHeight: 32,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  greetingEm: {
    fontFamily: Fonts.serifBold,
    fontStyle: 'italic',
  },
  sub: {
    marginTop: 3,
    fontSize: 13,
    color: Palette.inkSoft,
    fontFamily: Fonts.sansMedium,
  },
  // "at [Home Name]" tagline — multi-home only. Sits just under the
  // date so it reads as a continuation ("Friday, May 23 — at Lake
  // House") rather than a separate UI element. Tap routes to My Home
  // where the user can hit the switcher chevron to flip homes.
  activeHomeRow: {
    marginTop: 2,
  },
  activeHomeText: {
    fontSize: 13,
    fontFamily: Fonts.sansMedium,
    color: Palette.inkSoft,
  },
  activeHomeName: {
    fontFamily: Fonts.sansBold,
    color: Palette.forest,
  },
  // Outer wrapper: cancels the parent header's 22px side padding so the
  // ScrollView spans the real screen edges. marginTop carries the
  // vertical rhythm previously on `chips`.
  chipsScroll: {
    marginTop: 13,
    marginHorizontal: -22,
  },
  // Inner content: restore the 22px gutter so the first chip sits flush
  // with the greeting copy and the last chip can scroll a few pixels
  // off the page before bouncing. The right pad doubles as a "more
  // available" hint for scroll-bounce.
  chips: {
    flexDirection: 'row',
    gap: 8,
    paddingLeft: 22,
    paddingRight: 22,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 11,
    paddingVertical: 6,
    borderRadius: 999,
  },
  chipSeason: {
    backgroundColor: '#DCE7E1',
  },
  chipDone: {
    backgroundColor: Palette.honeySoft,
  },
  chipStreak: {
    backgroundColor: 'rgba(176,95,60,0.13)',
    borderWidth: 1,
    borderColor: 'rgba(176,95,60,0.3)',
  },
  // "Tune my plan" chip — honey-soft fill (same family as the "done
  // this week" chip but with an amber border to mark it as actionable
  // setup work, not a celebratory stat).
  chipTune: {
    backgroundColor: Palette.honeySoft,
    borderWidth: 1,
    borderColor: 'rgba(225,163,61,0.45)',
  },
  // "Finish my profile" chip — moss/forest tinted to read as the
  // ground-level home-details action (sibling to the season chip).
  // Distinct from Tune (subtype chips) so users can tell at a glance
  // whether they need to fill home attrs or pick system variations.
  chipFinish: {
    backgroundColor: '#E5EDE5',
    borderWidth: 1,
    borderColor: 'rgba(58,87,68,0.25)',
  },
  chipText: {
    fontSize: 12.5,
    fontFamily: Fonts.sansSemibold,
    letterSpacing: 0.1,
  },
  chipNum: {
    fontFamily: Fonts.sansBold,
  },
});
